import React, { useState } from 'react';
import { History } from 'lucide-react';
import { formatDateTime } from '../../utils/locale';
import { useLanguage } from '../../i18n/LanguageContext';

const LoginHistoryTab = ({ history, total, loading }) => {
  const ITEMS_PER_PAGE = 3;
  const { t, locale } = useLanguage();
  const [currentPage, setCurrentPage] = useState(0);
  const formatDate = (dateString) => {
    if (!dateString) return t('common.notAvailable');
    return formatDateTime(dateString, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }, locale);
  };

  const paginatedHistory = history.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );
  const totalPages = Math.max(1, Math.ceil(history.length / ITEMS_PER_PAGE));

  return (
    <div className="space-y-4">
      <div className="football-card glass-football p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-[#1e293b] flex items-center">
            <History className="w-6 h-6 mr-2" />
            {t('loginHistory.title')}
          </h2>
          <span className="text-sm text-gray-400">{t('loginHistory.total', { count: total })}</span>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">{t('loginHistory.loading')}</p>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8">
            <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-400">{t('loginHistory.empty')}</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {paginatedHistory.map((entry) => (
              <div
                key={entry.id}
                className={`p-4 rounded-lg border-2 ${
                  entry.success
                    ? 'border-green-500/30 bg-green-500/5'
                    : 'border-red-500/30 bg-red-500/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        entry.success
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {entry.success ? t('loginHistory.success') : t('loginHistory.failed')}
                      </span>
                      <span className="text-xs text-gray-400">{entry.loginMethod}</span>
                    </div>
                    <p className="text-sm text-gray-300">
                      {formatDate(entry.createdAt)}
                    </p>
                    {(entry.location || entry.ipAddress) && (
                      <p className="text-xs text-gray-400 mt-1">{t('loginHistory.location')}: {entry.location || entry.ipAddress}</p>
                    )}
                    {entry.failureReason && (
                      <p className="text-xs text-red-400 mt-1">{t('loginHistory.reason')}: {entry.failureReason}</p>
                    )}
                  </div>
                </div>
              </div>
              ))}
            </div>
            {history.length > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-center space-x-4 mt-6">
                <button
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    currentPage === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-yellow-400 text-black hover:bg-yellow-500'
                  }`}
                >
                  {t('common.previous')}
                </button>
                <span className="text-gray-400 text-sm">
                  {t('common.pageOf', { page: currentPage + 1, total: totalPages })}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    currentPage >= totalPages - 1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-yellow-400 text-black hover:bg-yellow-500'
                  }`}
                >
                  {t('common.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginHistoryTab;
